<template>
  <div class="export-content__settings">
    <div class="export-content__setting">
      <p v-if="isReadwiseEnabled" class="export-content__step-hint">
        Highlights export to Readwise is enabled.
      </p>
      <p v-else class="export-content__step-hint">
        To enable highlights export to Readwise, open the
        <a
          class="export-content__link"
          href="https://readwise.io/access_token"
          target="_blank"
          >Readwise Access Token page</a
        >, copy and paste the API token into the field below and click “Enable”
        button.
      </p>
      <form
        role="form"
        class="form export-content__form_readwise"
        novalidate
        @submit.prevent="checkForm"
      >
        <div class="export-content__form--section">
          <div class="export-content__label-wrapper">
            <label class="export-content__label" for="token">API key</label>
            <FormError :errors="validation.errors" field="readwise_token" />
          </div>
          <InputWithContextMenu
            ref="input"
            v-model="token"
            name="token"
            rows="1"
            type="text"
            class="export-content__input"
            placeholder="Readwise Token..."
            :disabled="isReadwiseEnabled"
            :class="validation.stateClass('readwise_token')"
          />
        </div>
        <div class="export-content__form--section">
          <div class="export-content__wrapper">
            <span class="export-content__wrapper--text">
              Integration allows exporting your highlights automatically.
            </span>
          </div>
          <div class="export-content__submit-buttons">
            <button
              v-if="!isReadwiseEnabled"
              v-wait:disabled="'Connecting'"
              type="submit"
              class="btns export-content__button-readwise"
            >
              <v-wait for="Connecting">
                <template v-slot:waiting>
                  Connecting
                  <button-spinner />
                </template>
                Connect and Export
              </v-wait>
            </button>
            <button
              v-if="isReadwiseEnabled"
              v-wait:disabled="'Disconnecting'"
              type="submit"
              class="btns export-content__button-readwise"
            >
              <v-wait for="Disconnecting">
                <template v-slot:waiting>
                  Disconnecting
                  <button-spinner />
                </template>
                Disconnect
              </v-wait>
            </button>
          </div>
        </div>
        <DisablePopup
          ref="popupDisable"
          integration-name="Readwise"
          :disableIntegration="view.disableIntegration"
          :afterDisableCallback="refresh"
          @updateEnabled="$emit('updateEnabled', false)"
        />
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import FormError from '@/components/ui/FormError.vue'
import { ValidationState } from '@/helpers/form'

import DisablePopup from '@/app/components/export/DisablePopup.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import { readwise } from '@/services/readwise'
import { assertNotNull } from '@/helpers/typing'

const validation = ref(new ValidationState())
const token = ref<string | null>(null)
const view = ref(readwise)

const props = withDefaults(
  defineProps<{
    isReadwiseEnabled?: boolean
  }>(),
  {
    isReadwiseEnabled: false,
  },
)

const emit = defineEmits<{
  (e: 'updateEnabled', value: boolean): void
}>()
const wait = useWait()

const popupDisable = ref<InstanceType<typeof DisablePopup> | null>(null)

onBeforeMount(async () => {
  await refresh()
  validation.value.reset()
})

async function refresh(): Promise<void> {
  token.value = await readwise.getToken()
  emit('updateEnabled', token.value !== null)
}

async function checkForm(): Promise<void> {
  if (!token.value || token.value.trim() === '') {
    validation.value.showMessage('readwise_token', 'Token may not be blank.')
    return
  }
  if (props.isReadwiseEnabled) {
    disableReadwise()
  } else {
    await enableReadwise()
  }
}

function getErrorMessage(error: any): string {
  // We already sent the error to the Sentry (see ReadwiseAPI._post).
  // Here we just show the error for a user.
  let errorMessage = 'Readwise integration error. Contact support, please.'

  // We can get error without response in case when the browser
  // received a 401 unauthorized response when doing the preflight
  // OPTION request, resulting in a Network Error.
  // See: https://stackoverflow.com/questions/49886315/axios-interceptors-response-undefined
  if (error.response === undefined) {
    errorMessage = 'Wrong Access Token: Authorization Failed.'
  }
  // Just in case added handling for 401 Authorization Error.
  // Not sure if this case will be at all or we always will
  // receive the previous case (an error without response),
  // but it is better to have this.
  if (error.response && error.response.status === 401) {
    errorMessage = 'Wrong Access Token: Authorization Failed.'
  }

  return errorMessage
}

async function enableReadwise(): Promise<void> {
  try {
    validation.value.reset()
    wait.start('Connecting')
    await readwise.enableAndExportAll(assertNotNull(token.value))
    emit('updateEnabled', true)
  } catch (error) {
    validation.value.showMessage('readwise_token', getErrorMessage(error))
  } finally {
    wait.end('Connecting')
  }
}

function disableReadwise(): void {
  popupDisable.value?.show()
}
</script>
